import { Identify } from '@amplitude/analytics-browser';
import moment from 'moment';
import { defaultEnvironment } from '../environment';
import { Place, StatsTypes, User, UserChallenge, UserPlace, UserReferenceTrip, UserReferenceTripOccurrence, UserRide, UserTracesStats, periodToParams } from '../models';
import { Trace, TraceProblem } from '../models/trace';
import { AnalyticsService } from './analytics';
import { AppService } from './app';
import { parseBadgeLevels } from './badge';
import { parseCommutingTripOccurrence, parseCommutingTripPotentialOccurrence } from './commuting-trip';
import { backendCyclingProfiles } from './cycling-profile';
import { parseChallenge, subGroupTypesMap } from './geogroup';
import { HttpService } from './http';
import { PartnerService, parsePartner } from './partner';
import { backendBikeTypes } from './route';
export class UserService {
  static async getUser(id) {
    try {
      const data = await HttpService.get('v1', `/users/${id}`);
      const user = parseUser(data);
      try {
        const event = new Identify();
        event.set('user_id', id);
        event.set('is_staff', user.isGeovelo || user.isEcoCompteur);
        AnalyticsService.identify(event);
      } catch (err) {
        console.error(err);
      }
      return user;
    } catch (err) {
      console.error('[UserService][getUser]', err);
      return null;
    }
  }
  static async getCurrentUser({
    userId: _userId
  } = {}) {
    let userId = _userId;
    if (!userId) {
      try {
        const userIdLSItem = localStorage.getItem('impersonate_user_id') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id');
        userId = userIdLSItem ? parseInt(userIdLSItem, 10) : null;
      } catch {
        console.error('localStorage access is denied');
      }
      if (!userId) return null;
    }
    this.currentUser = await this.getUser(userId);
    if (!this.currentUser) {
      HttpService.authorizationToken = null;
      try {
        localStorage.removeItem('impersonate_authorization_token');
        localStorage.removeItem('impersonate_user_id');
        localStorage.removeItem('authorization_token');
        localStorage.removeItem('user_id');
        sessionStorage.removeItem('authorization_token');
        sessionStorage.removeItem('user_id');
      } catch {
        console.error('localStorage access is denied');
      }
    }
    return this.currentUser || null;
  }
  static async getUserByEmail(email) {
    try {
      const data = await HttpService.get('v1', `/users`, [{
        key: 'email',
        value: email
      }]);
      return data[0] ? parseMinimalUser(data[0]) : null;
    } catch (err) {
      console.error('[UserService][getUserByEmail]', err);
      throw err;
    }
  }
  static async searchUsers({
    page,
    pageSize,
    query,
    search
  }) {
    try {
      const {
        results
      } = await HttpService.get('v2', `/users`, [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'search',
        value: search
      }, {
        key: 'query',
        value: query
      }]);
      return results.map(parseMinimalUser);
    } catch (err) {
      console.error('[UserService][searchUsers]', err);
      throw err;
    }
  }
  static async createUser(email) {
    try {
      const data = await HttpService.post('v1', `/users`, [], [], JSON.stringify({
        email: email
      }));
      return parseMinimalUser(data);
    } catch (err) {
      console.error('[UserService][createUser]', err);
      throw err;
    }
  }
  static async updateUser({
    profilePicture,
    ...props
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const formData = new FormData();
      Object.keys(props).forEach(key => formData.append(key, props[key]));
      if (profilePicture) formData.append('profile_picture', profilePicture);
      const data = await HttpService.put('v1', `/users/${this.currentUser.id}`, [], [], formData);
      return parseUser(data);
    } catch (err) {
      console.error('[UserService][updateUser]', err);
      throw err;
    }
  }
  static async updateUserPassword(props) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.put('v1', `/users/${this.currentUser.id}/password`, [], [], JSON.stringify(props));
      return true;
    } catch (err) {
      console.error('[UserService][updateUserPassword]', err);
      throw err;
    }
  }
  static async deleteUser() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.delete('v1', `/users/${this.currentUser.id}`);
      try {
        localStorage.removeItem('impersonate_authorization_token');
        localStorage.removeItem('impersonate_user_id');
        localStorage.removeItem('authorization_token');
        localStorage.removeItem('user_id');
        sessionStorage.removeItem('authorization_token');
        sessionStorage.removeItem('user_id');
      } catch {
        console.error('localStorage access is denied');
      }
      HttpService.authorizationToken = null;
      UserService.currentUser = null;
      try {
        const event = new Identify();
        AnalyticsService.identify(event);
      } catch (err) {
        console.error(err);
      }
      if ('google' in window) google.accounts.id.disableAutoSelect();
      return true;
    } catch (err) {
      console.error('[UserService][deleteUser]', err);
      throw err;
    }
  }
  static async requestData() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.post('v1', `/users/${this.currentUser.id}/gdpr`);
      return true;
    } catch (err) {
      console.error('[UserService][requestData]', err);
      throw err;
    }
  }
  static async getPartners({
    isAdmin,
    isSupport,
    withInProgressContract,
    contractTemplateCodes
  } = {}) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      let contractTemplateIds;
      if (contractTemplateCodes) {
        const templates = await PartnerService.getContractTemplates({
          query: '{id, code, title}'
        });
        contractTemplateIds = templates.filter(({
          code
        }) => code && contractTemplateCodes.includes(code)).map(({
          id
        }) => id);
      }
      const data = await HttpService.get('v3', `/users/${this.currentUser.id}/partners`);
      const partners = data.filter(({
        partner
      }) => {
        const contracts = partner.contracts?.filter(({
          contract_template,
          start_datetime,
          end_datetime
        }) => (!withInProgressContract || moment().isSameOrAfter(moment(start_datetime).startOf('day')) && moment().isSameOrBefore(moment(end_datetime).endOf('day'))) && (!contractTemplateIds || typeof contract_template === 'number' && contractTemplateIds.includes(contract_template))) || [];
        if ((withInProgressContract || contractTemplateIds) && contracts.length === 0) return false;
        return true;
      }).reduce((res, partnerData) => {
        const {
          partner: {
            contracts
          }
        } = partnerData;
        const filteredContracts = contracts?.filter(({
          contract_template,
          start_datetime,
          end_datetime
        }) => (!withInProgressContract || moment().isSameOrAfter(moment(start_datetime).startOf('day')) && moment().isSameOrBefore(moment(end_datetime).endOf('day'))) && (!contractTemplateIds || typeof contract_template === 'number' && contractTemplateIds.includes(contract_template))) || [];
        if ((withInProgressContract || contractTemplateIds) && filteredContracts.length === 0) return res;
        const partner = parsePartner(partnerData, isAdmin, isSupport);
        if (partner) res.push(partner);
        return res;
      }, []);
      return partners;
    } catch (err) {
      console.error('[UserService][getPartners]', err);
      return [];
    }
  }
  static async getUserTracesStats(period, {
    dataOnly
  } = {}) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.get('v2', `/users/${this.currentUser.id}/stats_traces`, [...periodToParams(period), {
        key: 'query',
        value: dataOnly ? '{data{unit}}' : '{count,distance,duration,vertical_gain,avg_speed,co2e,data{unit,distance}}'
      }]);
      return parseUserTracesStats(data);
    } catch (err) {
      console.error('[UserService][getUserTracesStats]', err);
      throw err;
    }
  }
  static async downloadUserTrace(id) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.get('v6', `/users/${this.currentUser.id}/traces/${id}`, [], [], null, {
        contentType: 'application/gpx+xml',
        responseType: 'blob'
      });
      return data;
    } catch (err) {
      console.error('[UserService][downloadUserTrace]', err);
      throw err;
    }
  }
  static async removeUserTrace(id, {
    deletionReason,
    deletionComment
  } = {}) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.delete('v4', `/users/${this.currentUser.id}/traces/${id}`, [], [], JSON.stringify({
        deletion_reason: deletionReason || 'UNKNOWN',
        deletion_comment: deletionComment || ''
      }));
      return true;
    } catch (err) {
      console.error('[UserService][removeUserTrace]', err);
      throw err;
    }
  }
  static async sendLogs(logs) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.post('v1', `/users/${this.currentUser.id}/logs`, [], [], JSON.stringify({
        logs,
        user: this.currentUser.id
      }));
      return true;
    } catch (err) {
      console.error('[UserService][sendLogs]', err);
      throw err;
    }
  }
  static async getPlaces() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.get('v1', `/users/${this.currentUser.id}/places`);
      const places = data.map(parseUserPlace);
      return places;
    } catch (err) {
      console.error('[UserService][getPlaces]', err);
      throw err;
    }
  }
  static async addPlace({
    type,
    point: geo_point,
    title,
    address,
    partnerId
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const types = {
        home: 'HOME',
        other: 'OTHER',
        work: 'WORK',
        transportModeChange: 'TRANSPORT_MODE_CHANGE'
      };
      const body = {
        type: types[type],
        geo_point,
        address
      };
      if (title) body.title = title;
      if (partnerId && type === 'work') body.enterprise_partner_id = partnerId;
      const data = await HttpService.post('v1', `/users/${this.currentUser.id}/places`, [], [], JSON.stringify(body));
      return parseUserPlace(data);
    } catch (err) {
      console.error('[UserService][addPlace]', err);
      throw err;
    }
  }
  static async updatePlace(id, {
    point: geo_point,
    title,
    address,
    partnerId
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const body = {
        geo_point,
        address
      };
      if (title) body.title = title;
      if (partnerId !== undefined) body.enterprise_partner_id = partnerId;
      const data = await HttpService.patch('v1', `/users/${this.currentUser.id}/places/${id}`, [], [], JSON.stringify(body));
      return parseUserPlace(data);
    } catch (err) {
      console.error('[UserService][updatePlace]', err);
      throw err;
    }
  }
  static async removePlace(id) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.delete('v1', `/users/${this.currentUser.id}/places/${id}`);
    } catch (err) {
      console.error('[UserService][removePlace]', err);
      throw err;
    }
  }
  static async joinChallenge({
    geogroupId,
    challengeId
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.post('v2', `/users/${this.currentUser.id}/geogroups/${geogroupId}/challenges`, [], [], JSON.stringify({
        challenge: challengeId
      }));
      const userChallenge = parseUserChallenge(data, AppService.environment.backendUrl);
      if (!userChallenge) throw new Error('challenge type not taken into account');
      return userChallenge;
    } catch (err) {
      console.error('[UserService][joinChallenge]', err);
      throw err;
    }
  }
  static async getUserChallenge(geogroupId, challengeId) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.get('v2', `/users/${this.currentUser.id}/geogroups/${geogroupId}/challenges/${challengeId}`);
      const userChallenge = parseUserChallenge(data, AppService.environment.backendUrl);
      if (!userChallenge) throw new Error('challenge type not taken into account');
      return userChallenge;
    } catch (err) {
      console.error('[UserService][getUserChallenge]', err);
      throw new Error('not found');
    }
  }
  static async getUserChallenges({
    groupIds,
    challengeIds,
    page,
    pageSize,
    query,
    eventId
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const queryParams = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'target_type[]',
        value: 'TRAVELED_DISTANCE'
      }, {
        key: 'target_type[]',
        value: 'CYCLING_DAYS'
      }, {
        key: 'target_type[]',
        value: 'TRACES_COUNT'
      }, {
        key: 'target_type[]',
        value: 'NO_TARGET'
      }, {
        key: 'target_type[]',
        value: 'BTTF_POINTS'
      }, {
        key: 'target_type[]',
        value: 'CUSTOM'
      }];
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      if (groupIds) queryParams.push({
        key: 'group_id',
        value: groupIds.join(',')
      });
      if (challengeIds) queryParams.push({
        key: 'challenge_id',
        value: challengeIds.join(',')
      });
      if (eventId) queryParams.push({
        key: 'event_id',
        value: eventId
      });
      const {
        results
      } = await HttpService.get('v6', `/users/${this.currentUser.id}/group_challenges_progresses`, queryParams);
      return results.reduce((res, data) => {
        const userChallenge = parseUserChallengeV5(data, AppService.environment.backendUrl);
        if (userChallenge) res.push(userChallenge);
        return res;
      }, []);
    } catch (err) {
      console.error('[UserService][getUserChallenges]', err);
      throw err;
    }
  }
  static async leaveChallenge(geogroupId, challengeId) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.delete('v2', `/users/${this.currentUser.id}/geogroups/${geogroupId}/challenges/${challengeId}`);
    } catch (err) {
      console.error('[UserService][removeRide]', err);
    }
  }
  static async getRides() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.get('v2', `/users/${this.currentUser.id}/rides`);
      const rides = data.reduce((res, rideData) => {
        const ride = parseUserRide(rideData);
        if (ride) res.push(ride);
        return res;
      }, []);
      return rides;
    } catch (err) {
      console.error('[UserService][getRides]', err);
      return [];
    }
  }
  static async addRide(id) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.post('v2', `/users/${this.currentUser.id}/rides`, [], [], JSON.stringify({
        ride_id: id
      }));
      return parseUserRide(data);
    } catch (err) {
      console.error('[UserService][addRide]', err);
      return null;
    }
  }
  static async removeRide(id) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.delete('v2', `/users/${this.currentUser.id}/rides/${id}`);
    } catch (err) {
      console.error('[UserService][removeRide]', err);
    }
  }
  static async updateUserOptions(userId, props) {
    try {
      const data = await HttpService.put('v1', `/users/${userId}/options`, [], [], JSON.stringify(props));
      const options = parseUserOptions(data);
      return options;
    } catch (err) {
      console.error('[UserService][updateUserOptions]', err);
      throw err;
    }
  }
  static async updateUserParameters({
    language,
    ..._props
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const props = _props;
      if (language) {
        props.language = languageCodes[language];
      }
      const data = await HttpService.put('v1', `/users/${this.currentUser.id}/parameters`, [], [], JSON.stringify(props));
      const parameters = parseUserParameters(data);
      return parameters;
    } catch (err) {
      console.error('[UserService][updateUserParameters]', err);
      throw err;
    }
  }
  static async getReferenceTrips({
    page,
    pageSize,
    orderBy,
    order,
    query
  }) {
    try {
      const params = [];
      if (!this.currentUser) throw new Error('user not connected');
      if (orderBy && order) {
        let value;
        if (orderBy === 'id') value = 'id';
        if (value) {
          if (order === 'desc') value = `-${value}`;
          params.push({
            key: 'ordering',
            value
          });
        }
      }
      const {
        count,
        next,
        results
      } = await HttpService.get('v3', `/users/${this.currentUser.id}/reference_trips`, [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'query',
        value: query
      }, ...params]);
      return {
        count,
        next,
        referenceTrips: results.map(parseUserReferenceTrip)
      };
    } catch (err) {
      console.error('[ReferenceTripService][getReferenceTrips]', err);
      throw err;
    }
  }
  static async addReferenceTripOccurrence(referenceTrip, {
    date,
    direction
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.post('v3', `/users/${this.currentUser.id}/reference_trips/${referenceTrip.id}/occurrences`, [], [], JSON.stringify({
        date,
        comment: '',
        direction: toBackendTripDirection(direction)
      }));
      return parseUserReferenceTripOccurrence(data);
    } catch (err) {
      console.error('[ReferenceTripOccurrenceService][addReferenceTripOccurrence]', err);
      return null;
    }
  }
  static async updateUserReferenceTripOccurrence({
    id,
    userReferenceTripId
  }, {
    date,
    comment,
    direction,
    enabled
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const params = {
        date,
        direction: toBackendTripDirection(direction),
        enabled
      };
      if (comment !== undefined) params.comment = comment;
      const data = await HttpService.put('v3', `/users/${this.currentUser.id}/reference_trips/${userReferenceTripId}/occurrences/${id}`, [], [], JSON.stringify(params));
      return parseUserReferenceTripOccurrence(data);
    } catch (err) {
      console.error('[ReferenceTripOccurrenceService][updateReferenceTripOccurrence]', err);
      throw err;
    }
  }
  static async removeReferenceTripOccurrence({
    userReferenceTripId,
    id
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.delete('v3', `/users/${this.currentUser.id}/reference_trips/${userReferenceTripId}/occurrences/${id}`);
      return true;
    } catch (err) {
      console.error('[ReferenceTripOccurrenceService][removeReferenceTripOccurrence]', err);
      throw err;
    }
  }
  static async getTraceProblems(traceId) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const data = await HttpService.get('v2', `/users/${this.currentUser.id}/traces/${traceId}/user_trace_problems`);
      return data.map(parseTraceProblem);
    } catch (err) {
      console.error('[UserService][getTraceProblem]', err);
      return [];
    }
  }
  static async getTraces({
    user,
    period,
    ordering,
    page,
    rowsPerPage,
    condensed,
    query,
    retrieveProblems
  }) {
    try {
      if (!user && !this.currentUser) throw new Error('user not connected');
      const params = [];
      if (period) {
        params.push({
          key: 'period',
          value: 'custom'
        }, {
          key: 'date_start',
          value: period.from.format('DD-MM-yyyy')
        }, {
          key: 'date_end',
          value: period.to.format('DD-MM-yyyy')
        });
      }
      if (ordering) params.push({
        key: 'ordering',
        value: ordering
      });
      if (page) params.push({
        key: 'page',
        value: page
      });
      if (rowsPerPage) params.push({
        key: 'page_size',
        value: rowsPerPage
      });
      if (condensed) params.push({
        key: 'condensed',
        value: condensed
      });
      if (query) params.push({
        key: 'query',
        value: query
      });
      const {
        count,
        results
      } = await HttpService.get('v6', `/users/${user?.id || this.currentUser?.id}/traces`, params);
      const problems = await Promise.all(results.map(({
        id,
        has_problem
      }) => retrieveProblems && has_problem ? UserService.getTraceProblems(id) : []));
      return {
        count,
        results: results.map((trace, index) => parseTrace(trace, problems[index]))
      };
    } catch (err) {
      console.error('[UserService][getTraces]', err);
      throw err;
    }
  }
  static async getSimplifiedTraces({
    period
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const params = [{
        key: 'date_start',
        value: period.from.format('YYYY-MM-DD')
      }, {
        key: 'date_end',
        value: period.to.format('YYYY-MM-DD')
      }];
      const res = await HttpService.get('v2', `/users/${this.currentUser.id}/simplified_traces`, params);
      return res;
    } catch (err) {
      console.error('[UserService][getSimplifiedTraces]', err);
      throw err;
    }
  }
  static async getH3Cells() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const res = await HttpService.get('v1', `/users/${this.currentUser.id}/h3_zones`);
      return res.map(({
        h3_index_7: index,
        is_bigger_ring: isInBiggerRing
      }) => ({
        index,
        isInBiggerRing
      }));
    } catch (err) {
      console.error('[UserService][getH3Cells]', err);
      throw err;
    }
  }
  static async getTrace(userId, id) {
    try {
      const result = await HttpService.get('v6', `/users/${userId}/traces/${id}`);
      const problems = result.has_problem ? await UserService.getTraceProblems(id) : [];
      return parseTrace(result, problems);
    } catch (err) {
      console.error('[UserService][getTrace]', err);
      throw err;
    }
  }
  static async updateTrace(userId, traceId, {
    bicycleId,
    title,
    label
  }) {
    try {
      if (bicycleId) {
        await HttpService.post('v1', `/traces/${traceId}/bicycle`, [], [], JSON.stringify({
          bicycle_id: bicycleId
        }));
      }
      if (title !== undefined || label !== undefined) {
        await HttpService.patch('v6', `/users/${userId}/traces/${traceId}`, [], [], JSON.stringify({
          ...(title !== undefined ? {
            title
          } : {}),
          ...(label !== undefined ? {
            label: label ? backendTraceLabelsMap[label] : null
          } : {})
        }));
      }
      return UserService.getTrace(userId, traceId);
    } catch (err) {
      console.error('[UserService][updateTrace]', err);
      throw err;
    }
  }
  static async getTracePhotos(userId, traceId) {
    try {
      const {
        results
      } = await HttpService.get('v6', `/users/${userId}/traces/${traceId}/photos`, [{
        key: 'page_size',
        value: 100
      }]);
      return results.map(parseTracePhoto);
    } catch (err) {
      console.error('[UserService][getTracePhotos]', err);
      throw err;
    }
  }
  static async addTracePhoto(userId, traceId, {
    file,
    position
  }) {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      // Left out when not given: the backend reads the position from the photo's EXIF data.
      if (position) formData.append('position', JSON.stringify(toBackendPosition(position)));
      const result = await HttpService.post('v6', `/users/${userId}/traces/${traceId}/photos`, [], [], formData);
      return parseTracePhoto(result);
    } catch (err) {
      console.error('[UserService][addTracePhoto]', err);
      throw err;
    }
  }
  static async updateTracePhoto(userId, traceId, photoId, {
    order,
    position
  }) {
    try {
      // Sent as `multipart/form-data`, like `addTracePhoto`: the GeoJSON `position` field is a
      // Django GIS `PointField`, and its parser is the one exercised by the upload — a JSON body
      // went through DRF's JSON parser instead and the update failed there.
      const formData = new FormData();
      if (order !== undefined) formData.append('order', String(order));
      if (position !== undefined) formData.append('position', position ? JSON.stringify(toBackendPosition(position)) : '');
      const result = await HttpService.patch('v6', `/users/${userId}/traces/${traceId}/photos/${photoId}`, [], [], formData);
      return parseTracePhoto(result);
    } catch (err) {
      console.error('[UserService][updateTracePhoto]', err);
      throw err;
    }
  }
  static async deleteTracePhoto(userId, traceId, photoId) {
    try {
      await HttpService.delete('v6', `/users/${userId}/traces/${traceId}/photos/${photoId}`, [], [], null, {
        expectedStatus: 204
      });
      return true;
    } catch (err) {
      console.error('[UserService][deleteTracePhoto]', err);
      throw err;
    }
  }
  static async tagTraceAsHomeWork({
    userTraceId,
    referenceTripOccurrenceId: occurrence_id
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const {
        commuting_trip_occurrence: data
      } = await HttpService.post('v6', `/users/${this.currentUser.id}/user_trace/${userTraceId}/commuting`, [], [], JSON.stringify({
        occurrence_id
      }));
      return data ? parseCommutingTripOccurrence(data) : undefined;
    } catch (err) {
      console.error('[UserService][tagTraceAsHomeWork]', err);
      throw err;
    }
  }
  static async untagHomeWorkTrace({
    userTraceId
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.delete('v6', `/users/${this.currentUser.id}/user_trace/${userTraceId}/commuting`, [], [], null, {
        expectedStatus: 204
      });
      return true;
    } catch (err) {
      console.error('[UserService][untagHomeWorkTrace]', err);
      throw err;
    }
  }
  static async tagTraceAsBike({
    userTrace
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.patch('v2', `/traces/${userTrace.id}/transport_mode`, [], [], JSON.stringify({
        is_bike_manually_classified: true
      }));
      return true;
    } catch (err) {
      console.error('[UserService][tagTraceAsBike]', err);
      throw err;
    }
  }
  static async getRawTrace(userId, traceId) {
    try {
      const {
        id
      } = await HttpService.get('v2', `/users/${userId}/traces/${traceId}/raw_user_trace`);
      return id;
    } catch (err) {
      console.error('[UserService][getRawTrace]', err);
      throw err;
    }
  }
  static async getTracePreviewUrl({
    traceId
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const {
        url
      } = await HttpService.post('v2', `/users/${this.currentUser.id}/traces/${traceId}/preview`);
      return url;
    } catch (err) {
      console.error('[UserService][getTracePreviewUrl]', err);
      throw err;
    }
  }
  static async getLogs(userId) {
    try {
      const results = await HttpService.get('v1', `/users/${userId}/logs`);
      return results[0].logs || null;
    } catch (err) {
      console.error('[UserService][getLogs]', err);
      throw err;
    }
  }
  static async updateUserTraceGroundTruth({
    traceId,
    groundTruth
  }) {
    try {
      await HttpService.put('v2', `/traces/${traceId}/groundtruth`, [], [], JSON.stringify({
        ground_truth: groundTruth
      }));
      return true;
    } catch (err) {
      console.error('[UserService][updateUserTraceGroundTruth]', err);
      throw err;
    }
  }
  static async importGPXTrace(gpx, title) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const formData = new FormData();
      formData.append('gpx', gpx);
      formData.append('title', title);
      const {
        id
      } = await HttpService.post('v2', `/user_trace_from_gpx`, [], [], formData, {
        expectedStatus: 201
      });
      return UserService.getTrace(this.currentUser.id, id);
    } catch (err) {
      console.error('[UserService][importGPXTrace]', err);
      if (err instanceof Object && 'id' in err) throw 'AlreadyExists';
      if (err === 'Missing at least time information of one point in this GPX' || err === 'Too few points contained in this GPX file') throw 'MissingData';
      throw new Error(err.error);
    }
  }
  static async generateUsername() {
    try {
      const {
        username
      } = await HttpService.get('v1', `/username_generator`);
      return username;
    } catch (err) {
      console.error('[UserService][generateUsername]', err);
      throw err;
    }
  }
  static async getUserGameLevel() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const {
        main_level_points,
        level,
        level_thresholds,
        badges_contribution
      } = await HttpService.get('v2', `/users/${this.currentUser.id}/game_main_level`);
      return {
        mainLevelPoints: main_level_points,
        level,
        levelThresholds: level_thresholds || [],
        pointsDetails: badges_contribution?.map(({
          code: badgeKey,
          main_level_points: points
        }) => ({
          badgeKey,
          points
        })) || undefined
      };
    } catch (err) {
      console.error('[UserService][getUserGameLevel]', err);
      throw err;
    }
  }
  static async getPublicProfile({
    userId
  }) {
    try {
      const res = await HttpService.get('v2', `/users/${userId}/public_profile`);
      return parsePublicProfile(res);
    } catch (err) {
      console.error('[UserService][getPublicProfile]', err);
      throw err;
    }
  }
  static async impersonate({
    userId,
    totpToken
  }) {
    try {
      const {
        key: token
      } = await HttpService.get('v1', `/user_impersonate`, [{
        key: 'user_id',
        value: userId
      }, {
        key: 'totp_token',
        value: totpToken
      }]);
      return {
        token
      };
    } catch (err) {
      console.error('[UserService][impersonate]', err);
      throw err;
    }
  }
  static async getBestEfforts() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const {
        max_distance_trace: maxDistanceTrace,
        max_duration_trace: maxDurationTrace,
        max_altitude_gain_trace
      } = await HttpService.get('v1', `/users/${this.currentUser.id}/personal_best`);
      return {
        maxDistanceTrace,
        maxDurationTrace,
        maxVerticalGainTrace: max_altitude_gain_trace ? {
          id: max_altitude_gain_trace.id,
          verticalGain: max_altitude_gain_trace.vertical_gain
        } : null
      };
    } catch (err) {
      console.error('[UserService][getBestEfforts]', err);
      throw err;
    }
  }
  static async getDashboardUserProfile() {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      const {
        first_name: firstName,
        last_name: lastName,
        concerned_by_tourism: concernedByTourism,
        concerned_by_companies: concernedByCompanies,
        concerned_by_communication: concernedByCommunication,
        concerned_by_sig: concernedBySig,
        concerned_by_facilities_deployment: concernedByFacilitiesDeployment,
        concerned_by_evolution_of_practice: concernedByEvolutionOfPractice,
        concerned_by_street_maintenance: concernedByStreetMaintenance,
        data_proficiency_level: dataProficiencyLevel
      } = await HttpService.get('v1', `/users/${this.currentUser.id}/dashboard_user_profile`);
      return {
        firstName,
        lastName,
        concernedByTourism,
        concernedByCompanies,
        concernedByCommunication,
        concernedBySig,
        concernedByFacilitiesDeployment,
        concernedByEvolutionOfPractice,
        concernedByStreetMaintenance,
        dataProficiencyLevel
      };
    } catch (err) {
      console.error('[UserService][getDashboardUserProfile]', err);
      throw err;
    }
  }
  static async updateDashboardUserProfile({
    dashboardUserProfile
  }) {
    try {
      if (!this.currentUser) throw new Error('user not connected');
      await HttpService.put('v1', `/users/${this.currentUser.id}/dashboard_user_profile`, [], [], JSON.stringify({
        first_name: dashboardUserProfile.firstName,
        last_name: dashboardUserProfile.lastName,
        concerned_by_tourism: dashboardUserProfile.concernedByTourism,
        concerned_by_companies: dashboardUserProfile.concernedByCompanies,
        concerned_by_communication: dashboardUserProfile.concernedByCommunication,
        concerned_by_sig: dashboardUserProfile.concernedBySig,
        concerned_by_facilities_deployment: dashboardUserProfile.concernedByFacilitiesDeployment,
        concerned_by_evolution_of_practice: dashboardUserProfile.concernedByEvolutionOfPractice,
        concerned_by_street_maintenance: dashboardUserProfile.concernedByStreetMaintenance,
        data_proficiency_level: dashboardUserProfile.dataProficiencyLevel
      }));
      return true;
    } catch (err) {
      console.error('[UserService][updateDashboardUserProfile]', err);
      throw err;
    }
  }
}
function parseUserOptions({
  dashboard_newsletter: dashboardNewsletter,
  receive_email: receiveEmails,
  receive_newsletter: receiveNewsletter
}) {
  return {
    dashboardNewsletter,
    receiveEmails,
    receiveNewsletter
  };
}
export const languageCodes = {
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE'
};
const languagesByCountry = {
  en: 'en',
  fr: 'fr',
  es: 'es',
  de: 'de'
};
function parseUserParameters({
  averageSpeed,
  bikeType,
  e_bike: eBike,
  paved_only: pavedOnly,
  profile,
  language: _language
}) {
  const specificLanguagesMap = {
    // 'fr-CH': 'fr-CH'
  };
  const country = _language && _language.length >= 2 && _language.substr(0, 2).toLowerCase() || null;
  const language = specificLanguagesMap[_language] || country && languagesByCountry[country] || 'fr';
  return {
    averageSpeed,
    bikeType: backendBikeTypes[bikeType],
    eBike,
    // The road profile is replaced by the paved only option
    pavedOnly: Boolean(pavedOnly) || profile === 'ROAD',
    profile: profile === 'ROAD' ? 'daily' : backendCyclingProfiles[profile],
    language
  };
}
function parseUser({
  id,
  email,
  username,
  date_of_birth,
  first_name,
  last_name,
  is_admin,
  is_support,
  is_staff,
  options,
  parameters,
  created,
  phone_number,
  profile_picture
}) {
  return new User(id, email, username || null, date_of_birth ? new Date(date_of_birth) : null, first_name || null, last_name || null, is_admin || false, is_support || false, is_staff || false, parseUserOptions(options), parseUserParameters(parameters), moment(created), phone_number || null, profile_picture ? `${defaultEnvironment.backendUrl}${profile_picture}` : undefined);
}
function parseMinimalUser({
  id,
  email,
  username,
  created,
  employee
}) {
  return new User(id, email || '', username || null, null, null, null, false, false, false, {
    dashboardNewsletter: false,
    receiveEmails: false,
    receiveNewsletter: false
  }, {
    averageSpeed: 15,
    bikeType: 'own',
    eBike: false,
    language: 'fr',
    pavedOnly: false,
    profile: 'daily'
  }, moment(created), null, undefined, employee?.map(({
    id,
    first_name,
    last_name,
    professional_email,
    partner
  }) => ({
    id,
    firstName: first_name,
    lastName: last_name,
    professionalEmail: professional_email,
    partner
  })));
}
function parseUserTracesStats({
  count,
  distance,
  duration,
  co2e,
  avg_speed,
  vertical_gain,
  data
}) {
  return new UserTracesStats(count || 0, distance || 0, duration || 0, avg_speed || 0, vertical_gain || 0, co2e !== null && co2e !== undefined ? co2e / 1000 : (distance || 0) / 1000 * defaultEnvironment.savedKgOfCO2PerKilometer, (data ?? []).map(({
    distance,
    unit: _unit
  }) => ({
    distance: distance || 0,
    unit: moment(_unit),
    _unit
  })));
}
function parseUserPlace({
  id,
  type,
  title,
  geo_point,
  address,
  created,
  commuting_trips_to_this_work
}) {
  const types = {
    HOME: 'home',
    OTHER: 'other',
    WORK: 'work',
    TRANSPORT_MODE_CHANGE: 'transportModeChange'
  };
  return new UserPlace(id, types[type], title, geo_point, address, moment(created), commuting_trips_to_this_work?.map(({
    id,
    home,
    work,
    enabled,
    employee
  }) => ({
    id,
    homeId: home,
    workId: work,
    enabled,
    partnerId: employee.partner
  })));
}
function parseUserChallenge({
  challenge: _challenge,
  progress_value,
  rank,
  leaderboard_index
}, backendUrl) {
  const challenge = parseChallenge(_challenge, backendUrl);
  if (!challenge) return null;
  return new UserChallenge(challenge, progress_value, rank, leaderboard_index ? leaderboard_index - 1 : undefined);
}
function parseUserChallengeV5({
  challenge: _challenge,
  progress_value,
  leaderboard
}, backendUrl) {
  const challenge = parseChallenge(_challenge, backendUrl);
  if (!challenge) return null;
  return new UserChallenge(challenge, progress_value, leaderboard?.rank, leaderboard?.index ? leaderboard.index - 1 : undefined, leaderboard?.subgroups?.filter(({
    is_member
  }) => is_member).map(({
    id: subGroupId,
    count_subgroup_of_same_type: countSubgroupOfSameType,
    progress_value: progressValue,
    rank,
    sub_group_type,
    title
  }) => ({
    subGroupId,
    countSubgroupOfSameType,
    progressValue,
    rank,
    subGroupType: sub_group_type && subGroupTypesMap[sub_group_type] || 'companyTeam',
    title
  })));
}
function parseUserRide({
  created,
  ride: rideId
}) {
  return new UserRide(rideId, moment(created));
}
export function parseUserReferenceTrip({
  id,
  created,
  title,
  geo_start,
  geo_start_title,
  geo_end,
  geo_end_title,
  distance_in_meters_start_end,
  distance_in_meters_end_start,
  enabled,
  start_user_place_type,
  end_user_place_type
}) {
  function toPlace(geoPoint, address) {
    return new Place(undefined, geoPoint, {
      primaryText: address
    });
  }
  return new UserReferenceTrip(id, moment(created), title, start_user_place_type === 'WORK' || end_user_place_type === 'HOME' ? toPlace(geo_end, geo_end_title) : toPlace(geo_start, geo_start_title), start_user_place_type === 'WORK' || end_user_place_type === 'HOME' ? toPlace(geo_start, geo_start_title) : toPlace(geo_end, geo_end_title), distance_in_meters_start_end, distance_in_meters_end_start, enabled);
}
export function parseUserReferenceTripOccurrence({
  id,
  created,
  date,
  certified_by_geovelo,
  manually_created,
  comment,
  direction,
  user_trace,
  enabled,
  user_reference_trip
}) {
  return new UserReferenceTripOccurrence(id, user_reference_trip, moment(created), moment(date), date, Boolean(certified_by_geovelo), manually_created && !user_trace, comment, toTripDirection(direction), Boolean(user_trace), enabled);
}
export function toBackendTripDirection(direction) {
  if (direction === 'outward') return 'OUTWARD';
  return 'RETURN';
}
export function toTripDirection(direction) {
  if (direction === 'OUTWARD') return 'outward';
  return 'return';
}
const traceRecordSources = {
  ACTIVITY_DETECTION: 'activityDetection',
  IMPORT_GPX: 'importedGPX',
  IMPORT_GARMIN: 'importedGarmin',
  IMPORT_WAHOO: 'importedWahoo',
  IMPORT_STRAVA: 'importedStrava',
  MANUAL: 'manual',
  NAVIGATION: 'navigation',
  NAVIGATION_RIDE: 'navigationRide',
  UNKNOWN: 'unknown'
};
function toBackendPosition(position) {
  return {
    type: 'Point',
    coordinates: position
  };
}

// The create response returns `photo` as an already-absolute URL, unlike every other endpoint
// (list/retrieve/update) which returns a path relative to `backendUrl` — prefixing it a second
// time there is what left the thumbnail broken until the next reload.
function resolvePhotoUrl(path) {
  return /^https?:\/\//.test(path) ? path : `${defaultEnvironment.backendUrl}${path}`;
}
function parseTracePhoto({
  id,
  photo,
  thumbnail,
  order,
  position
}) {
  return {
    id,
    order,
    position: position ? position.coordinates : null,
    thumbnailUrl: resolvePhotoUrl(thumbnail || photo),
    url: resolvePhotoUrl(photo)
  };
}
const traceLabelsMap = {
  PRO: 'pro',
  LEISURE: 'leisure',
  DAILY: 'daily',
  OTHER: 'other'
};
const backendTraceLabelsMap = {
  pro: 'PRO',
  leisure: 'LEISURE',
  daily: 'DAILY',
  other: 'OTHER'
};
export const userTraceDeletionReasons = ['NOT_BIKE', 'GEOMETRY_NOT_EXACT', 'DUPLICATE', 'OTHER'];
const consideredProblems = ['highSpeed', 'notBicycle', 'tooLong'];
function parseTrace({
  id,
  title,
  naive_local_start_datetime,
  naive_local_end_datetime,
  geometry,
  distance,
  cleaned_distance,
  duration,
  cleaned_duration,
  vertical_gain,
  cleaned_vertical_gain,
  average_speed,
  cleaned_avg_speed,
  speeds,
  elevations,
  computed_route_id,
  preview,
  preview_300_200,
  record_source,
  device_model_name,
  has_problem,
  challenges,
  clean_status,
  commuting_status,
  user_reference_trip_occurrence,
  usertracegameprogress,
  is_bike_automatically_classified,
  is_bike_manually_classified,
  bicycle,
  strava_activity,
  label
}, problems) {
  const isPotentiallyBike = clean_status === 'CLEAN' && is_bike_automatically_classified === false && is_bike_manually_classified === null;
  return new Trace(id, title || moment(naive_local_start_datetime).format('DD_MMM_hh[h]mm') + '_-_' + moment(naive_local_end_datetime).format('hh[h]mm'), moment(naive_local_start_datetime), moment(naive_local_end_datetime), geometry, distance || distance === 0 ? distance : cleaned_distance, duration || duration === 0 ? duration : cleaned_duration, vertical_gain || vertical_gain === 0 ? vertical_gain : cleaned_vertical_gain, average_speed || average_speed === 0 ? average_speed : cleaned_avg_speed, speeds, elevations, !!computed_route_id, preview || null, preview_300_200 || null, has_problem, problems?.filter(({
    problemType,
    fixDatetime
  }) => consideredProblems.includes(problemType) && !fixDatetime) || [], traceRecordSources[record_source] || 'unknown', device_model_name || null, commuting_status === 'COMMUTING' ? 'commuting' : commuting_status === 'COMMUTING_CANDIDATE' ? 'candidate' : commuting_status === 'FORCE_NOT_COMMUTING' ? 'forcedNotCommuting' : 'notCommuting', challenges, clean_status === 'CLEAN' ? 'cleaned' : clean_status === 'NO_CLEAN' ? 'not_cleaned' : undefined, (user_reference_trip_occurrence || []).map(parseCommutingTripPotentialOccurrence), {
    longDistance: usertracegameprogress?.distance_over_50km || false,
    verticalGain: usertracegameprogress?.altitude_gain_over_100 || false,
    nightBird: usertracegameprogress?.during_night || false
  }, isPotentiallyBike, bicycle || null, strava_activity?.activity_id ? {
    activityId: strava_activity.activity_id,
    source: strava_activity.source
  } : null, label && traceLabelsMap[label] || null);
}
const problemTypes = {
  CLEAN_FAILURE: 'cleanFailure',
  GEOMETRY_JUMP: 'geometryJump',
  HIGH_SPEED: 'highSpeed',
  LONG_DURATION: 'tooLong',
  MERGING: 'merging',
  NOT_BICYCLE: 'notBicycle',
  SHORT_DISTANCE: 'shortDistance'
};
function parseTraceProblem({
  problem_type,
  fix_datetime
}) {
  return new TraceProblem(problemTypes[problem_type], fix_datetime ? new Date(fix_datetime) : null);
}
function parsePublicProfile({
  username,
  profile_picture,
  count_traces,
  total_distance_in_meters,
  total_duration_in_seconds,
  average_speed_kmph,
  usergameprogress: {
    main_level,
    level_badges,
    achievements
  }
}) {
  return {
    user: {
      profilePicture: profile_picture ? `${defaultEnvironment.backendUrl}${profile_picture}` : profile_picture,
      username
    },
    gameLevel: main_level ? {
      mainLevelPoints: main_level.main_level_points,
      level: main_level.level,
      levelThresholds: main_level.level_thresholds || []
    } : undefined,
    stats: {
      [StatsTypes.Count]: count_traces,
      [StatsTypes.Distance]: total_distance_in_meters,
      [StatsTypes.Duration]: total_duration_in_seconds,
      [StatsTypes.VerticalGain]: 0,
      [StatsTypes.AverageSpeed]: average_speed_kmph
    },
    badgeLevels: parseBadgeLevels({
      level_badges: level_badges || []
    }),
    daysBadges: (achievements || []).map(({
      title: key,
      date
    }) => ({
      key,
      date: moment(date)
    }))
  };
}